import { Box, CircularProgress, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { generatePDF, Margin, Resolution } from '@/hooks/useGeneratePDF';

import { Button } from '@/components/Button';
import { Chart } from '@/components/Chart';
import { Data } from '@/components/Chart/types';
import InitialLoader from '@/components/Loader/InitialLoader';

import { getUnitUsageData } from './api';
import { IReport } from './types';
import { buildReportEstimatedData } from './util';

// File-system-friendly filename token: collapse anything that isn't alphanumeric
// to underscores. Mirrors the same pattern used in Billing/BreakdownDialog.
const sanitizeForFilename = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '_');

const ReportPage = () => {
  const [searchParams] = useSearchParams();
  const [encryptedData] = useState(searchParams.get('d'));
  const [reportData, setReportData] = useState<IReport>();
  const [data, setData] = useState<Data>();
  const [estimatedData, setEstimatedData] = useState<Data>([]);
  const [generatingPDF, setGeneratingPDF] = useState(false); // State to track PDF generation

  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const getData = async () => {
      const base64Str: string = decodeURIComponent(encryptedData || '');
      const data = await getUnitUsageData(base64Str);
      setReportData(data);
    };
    if (encryptedData !== null && reportData === undefined) getData();
  }, [encryptedData, reportData]);

  const formatDate = (epochSeconds: number, timeZone: string) =>
    new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timeZone,
    }).format(new Date(epochSeconds * 1000));

  useEffect(() => {
    if (reportData) {
      const {
        usage_daily_volumes,
        usage_start_epoch_seconds,
        usage_end_epoch_seconds,
        timezone_key,
      } = reportData.unit_usage_report;

      const generateDateRange = (
        startEpoch: number,
        endEpoch: number,
        _timeZone: string,
      ): string[] => {
        const dates: string[] = [];
        let currentDate = new Date(startEpoch * 1000);

        while (currentDate.getTime() <= endEpoch * 1000) {
          dates.push(currentDate.toISOString().slice(0, 10));
          currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
      };

      const dateArray: string[] = generateDateRange(
        usage_start_epoch_seconds,
        usage_end_epoch_seconds,
        timezone_key,
      );

      const usageData: Data = dateArray
        .map((date, index) => ({
          date: date,
          volume: usage_daily_volumes[index],
          gallons: usage_daily_volumes[index],
          label: date,
        }))
        .filter((entry) => entry.volume !== undefined && entry.gallons !== undefined);

      const filteredData = usageData.filter(
        (entry) => entry.volume !== undefined && entry.gallons !== undefined,
      );

      setData(filteredData);

      // Estimated overlay from the baked payload (empty for reports generated
      // before estimates shipped; see buildReportEstimatedData)
      setEstimatedData(
        buildReportEstimatedData(
          dateArray,
          reportData.unit_usage_report.usage_daily_estimated_volumes,
          reportData.unit_usage_report.usage_daily_estimated_reasons,
        ),
      );
    }
  }, [reportData]);

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    try {
      // Build a meaningful filename from the report context (property / unit / range).
      // Without this, useGeneratePDF.ts falls back to the literal string 'document.pdf',
      // which the browser then de-duplicates with numeric suffixes — surfacing as the
      // "random number" filename users see.
      const r = reportData?.unit_usage_report;
      const filename = r
        ? `${sanitizeForFilename(r.property_name)}_${sanitizeForFilename(
            r.unit_name,
          )}_usage_${formatDate(r.usage_start_epoch_seconds, r.timezone_key).replace(
            /\//g,
            '-',
          )}_to_${formatDate(r.usage_end_epoch_seconds, r.timezone_key).replace(/\//g, '-')}.pdf`
        : 'tenant_usage_report.pdf';
      await generatePDF(targetRef, {
        filename,
        method: 'save',
        resolution: Resolution.HIGH,
        page: { margin: Margin.MEDIUM, format: 'a4', orientation: 'portrait' },
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  return (
    <Box ref={targetRef} sx={{ typography: 'body1', padding: '16px', display: 'inline-block' }}>
      <Stack direction={'row'} spacing={5}>
        <Box sx={{ fontSize: '36px', fontWeight: 'bold' }}>Tenant Usage Report</Box>
        {/*
          Keep the button mounted while generating so it doesn't visibly flicker out
          and back in. data-html2canvas-ignore tells html2canvas to skip it during
          the PDF capture, so the rendered PDF still won't contain the button.
        */}
        <Button
          data-html2canvas-ignore="true"
          disabled={generatingPDF}
          onClick={() => handleGeneratePDF()}
        >
          {generatingPDF ? <CircularProgress size={20} color="inherit" /> : 'Download Report'}
        </Button>
      </Stack>
      {!reportData ? (
        <InitialLoader />
      ) : (
        <Box paddingTop={5}>
          <Stack direction={'column'} spacing={10}>
            <Box>
              <Box sx={{ fontSize: '20px', fontWeight: 'bold' }}>Unit Details</Box>
              <Stack spacing={1} paddingTop={2}>
                <Stack direction={'row'} spacing={1}>
                  <Box>Property Address: </Box>
                  <Box sx={{ fontWeight: 'bold' }}>
                    {reportData.unit_usage_report.property_name}
                  </Box>
                </Stack>
                <Stack direction={'row'} spacing={1}>
                  <Box>Unit Name: </Box>
                  <Box sx={{ fontWeight: 'bold' }}>{reportData.unit_usage_report.unit_name}</Box>
                </Stack>
                <Stack direction={'row'} spacing={1}>
                  <Box>Contact Name: </Box>
                  <Box sx={{ fontWeight: 'bold' }}>
                    {`${reportData.unit_usage_report.subscriber_first_name} ${reportData.unit_usage_report.subscriber_last_name}`.trim()}
                  </Box>
                </Stack>
                <Stack direction={'row'} spacing={1}>
                  <Box>Contact Email: </Box>
                  <Box sx={{ fontWeight: 'bold' }}>
                    {reportData.unit_usage_report.subscriber_email}
                  </Box>
                </Stack>
              </Stack>
            </Box>
            <Box>
              <Box sx={{ fontSize: '20px', fontWeight: 'bold' }}>Usage Details</Box>
              <Stack spacing={1} paddingTop={2}>
                <Stack direction={'row'} spacing={1}>
                  <Box>Usage Period: </Box>
                  <Box sx={{ fontWeight: 'bold' }}>
                    {formatDate(
                      reportData.unit_usage_report.usage_start_epoch_seconds,
                      reportData.unit_usage_report.timezone_key,
                    )}{' '}
                    -{' '}
                    {formatDate(
                      reportData.unit_usage_report.usage_end_epoch_seconds,
                      reportData.unit_usage_report.timezone_key,
                    )}{' '}
                    {reportData.unit_usage_report.timezone_key}
                  </Box>
                </Stack>
                <Stack direction={'row'} spacing={1}>
                  <Box>Water Usage: </Box>
                  <Box sx={{ fontWeight: 'bold' }}>
                    {(() => {
                      const report = reportData.unit_usage_report;
                      const estimatedTotal = (report.usage_daily_estimated_volumes ?? []).reduce(
                        (sum, volume) => sum + volume,
                        0,
                      );
                      if (estimatedTotal <= 0) {
                        return `${report.usage_total_volume} ${report.usage_volume_units}`;
                      }
                      // Combined total leads; the parenthetical decomposes it, the
                      // same convention as the report email and the invoice PDF
                      const combined =
                        Math.round((report.usage_total_volume + estimatedTotal) * 100) / 100;
                      const estimated = Math.round(estimatedTotal * 100) / 100;
                      return `${combined} ${report.usage_volume_units} (${report.usage_total_volume} metered + ${estimated} estimated)`;
                    })()}
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Stack>
          {reportData && data && (
            <Chart
              data={data}
              estimated={estimatedData}
              x="date"
              y="volume"
              title="Daily Water Usage (gallons)"
              style={{ maring: '0px !important', marginTop: '20px', width: '100%' }}
              yAxisType=""
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReportPage;
